import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SignaturePad from 'signature_pad';
import styles from '../stylesheets/signature-drawer.module.scss';
import s from '../stylesheets/signature-font.module.scss';
import { dataUrlToWixFile, decodeDataUrl, encodeDataUrl } from '../utils/data-url';
import { writeTextOnCanvas } from '../utils/text';
import { resizeCanvas } from '../utils/canvas';
import { focusOnPrevElement } from '../utils/focus';
import classNames from 'classnames';
import { SignatureFormat } from '../signature-types';
import { decodePoints, encodePoints } from '../utils/points';
import { useId } from '../../../hooks/use-id';
import { useTranslation } from '../../../contexts/translation-context';
import { useFocusFieldEvent } from '../../../contexts/events-context';
export const SignatureDrawer = props => {
  const {
    className,
    onChange: _onChange,
    value,
    theme,
    rtl,
    penColor,
    inputId,
    externalImage,
    onBeforeChange,
    'aria-required': ariaRequired,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    fieldId,
    ...rest
  } = props;
  const pad = React.useRef();
  const {
    t
  } = useTranslation();
  const inputRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const valueUrl = value == null ? void 0 : value.url;
  const {
    type,
    value: signatureText
  } = decodeDataUrl(valueUrl) ?? {};
  const drawable = !valueUrl || type === SignatureFormat.POINTS;
  const writable = drawable || type === SignatureFormat.TEXT;
  const [isDrawing, setIsDrawing] = useState(false);
  const options = useMemo(() => ({
    throttle: 0,
    dotSize: 2,
    minWidth: 1,
    maxWidth: 3,
    velocityFilterWeight: 0.2,
    penColor
  }), [penColor]);
  const getSignature = useCallback(() => {
    var _pad$current, _pad$current2, _pad$current3;
    if (type === SignatureFormat.TEXT) {
      return valueUrl ?? '';
    }
    if ((_pad$current = pad.current) != null && _pad$current.isEmpty()) {
      return '';
    }
    const dataPoints = (_pad$current2 = pad.current) == null ? void 0 : _pad$current2.toData();
    return dataPoints ? encodePoints(dataPoints) : ((_pad$current3 = pad.current) == null ? void 0 : _pad$current3.toDataURL()) ?? '';
  }, [type, valueUrl]);
  const onChange = useCallback(() => {
    setIsDrawing(false);
    _onChange == null || _onChange(dataUrlToWixFile(getSignature()));
  }, [getSignature, _onChange]);
  const writeOnCanvas = useCallback(newText => {
    var _pad$current4;
    (_pad$current4 = pad.current) == null || _pad$current4.clear();
    if (!newText || !canvasRef.current) {
      return;
    }
    writeTextOnCanvas(canvasRef.current, newText, options == null ? void 0 : options.penColor, rtl);
  }, [options == null ? void 0 : options.penColor, rtl]);
  const initCanvas = useCallback(() => {
    resizeCanvas(canvasRef.current);
    if (type === SignatureFormat.TEXT) {
      writeOnCanvas(signatureText ?? '');
    } else if (type === SignatureFormat.POINTS && valueUrl) {
      const points = decodePoints(valueUrl);
      if (points) {
        var _pad$current5;
        (_pad$current5 = pad.current) == null || _pad$current5.fromData(points);
      }
    } else {
      var _pad$current6;
      (_pad$current6 = pad.current) == null || _pad$current6.clear();
    }
  }, [signatureText, type, valueUrl, writeOnCanvas]);
  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }
    pad.current = new SignaturePad(canvasRef.current, options);
  }, [options]);
  useEffect(() => {
    if (!canvasRef.current || !pad.current) {
      return;
    }
    initCanvas();
    pad.current.addEventListener('endStroke', onChange);
    window.addEventListener('resize', initCanvas);
    return function () {
      var _pad$current7;
      window.removeEventListener('resize', initCanvas);
      (_pad$current7 = pad.current) == null || _pad$current7.removeEventListener('endStroke', onChange);
    };
  }, [onChange, initCanvas]);
  const handleBeforeChange = useCallback(() => {
    setIsDrawing(true);
    onBeforeChange == null || onBeforeChange();
  }, [onBeforeChange]);
  useEffect(() => {
    if (!pad.current) {
      return;
    }
    if (onBeforeChange) {
      pad.current.addEventListener('beginStroke', handleBeforeChange);
    }
    return () => {
      if (handleBeforeChange) {
        window.removeEventListener('beginStroke', handleBeforeChange);
      }
    };
  }, [handleBeforeChange, onBeforeChange]);
  useEffect(() => {
    if (drawable) {
      var _pad$current8;
      (_pad$current8 = pad.current) == null || _pad$current8.on();
    } else {
      var _pad$current9;
      (_pad$current9 = pad.current) == null || _pad$current9.off();
    }
  }, [drawable]);
  const handleTextChange = useCallback(event => {
    if (writable) {
      onBeforeChange == null || onBeforeChange();
      const text = event.target.value;
      writeOnCanvas(text);
      if (text.trim()) {
        _onChange == null || _onChange(dataUrlToWixFile(encodeDataUrl(event.target.value, SignatureFormat.TEXT)));
      } else {
        _onChange == null || _onChange(null);
      }
    }
  }, [writable, onBeforeChange, writeOnCanvas, _onChange]);
  const focusInput = useCallback(ev => {
    if (ev.relatedTarget !== inputRef.current) {
      var _inputRef$current;
      (_inputRef$current = inputRef.current) == null || _inputRef$current.focus();
    }
  }, []);
  const handleTextKeyDown = useCallback(ev => {
    const isBackTab = ev.shiftKey && ev.key === 'Tab';
    if (isBackTab) {
      // When you shift-tab the focus changes from the hidden input to the canvas
      // the user cannot notice a difference because it's the same focus-ring, so it feels like a bug
      // we skip the canvas and focus directly on the element before it
      focusOnPrevElement();
    }
  }, []);
  const screenReaderActionDescriptionId = useId();
  const ariaDescribedByIds = [ariaDescribedBy, screenReaderActionDescriptionId].filter(Boolean).join(' ');
  const screenReaderText = useMemo(() => {
    if (type === SignatureFormat.TEXT) {
      return signatureText;
    }
    if (!pad.current || pad.current.isEmpty()) {
      return t('field.signature.a11y.state.empty');
    }
    if (isDrawing) {
      return t('field.signature.a11y.state.drawing');
    }
    return t('field.signature.a11y.state.signed');
  }, [type, signatureText, pad, isDrawing, t]);
  const screenReaderActionDescription = useMemo(() => {
    if (drawable && writable) {
      return t('field.signature.a11y.action-description.draw-or-type');
    }
    if (writable) {
      return t('field.signature.a11y.action-description.type');
    }
    return t('field.signature.a11y.action-description.draw');
  }, [drawable, writable, t]);
  useFocusFieldEvent(() => {
    var _canvasRef$current;
    (_canvasRef$current = canvasRef.current) == null || _canvasRef$current.focus();
  }, fieldId);
  return /*#__PURE__*/React.createElement("div", {
    className: className,
    "data-theme": theme
  }, /*#__PURE__*/React.createElement("div", {
    id: screenReaderActionDescriptionId,
    className: styles.visuallyHidden,
    hidden: true
  }, screenReaderActionDescription), /*#__PURE__*/React.createElement("div", {
    className: styles.visuallyHidden,
    role: "alert"
  }, screenReaderText), /*#__PURE__*/React.createElement("canvas", _extends({}, rest, {
    tabIndex: 0,
    ref: canvasRef,
    onFocus: focusInput,
    onClick: focusInput,
    "data-input-id": inputId,
    "data-has-value": !!valueUrl,
    className: classNames(styles.canvas, {
      [styles.hidden]: !!externalImage
    }),
    "data-hook": "signature-canvas",
    "aria-hidden": "true"
  })), /*#__PURE__*/React.createElement("input", {
    type: "text",
    tabIndex: -1,
    ref: inputRef,
    id: inputId,
    "data-input-id": inputId,
    value: type === SignatureFormat.TEXT ? signatureText : '',
    onChange: handleTextChange,
    onKeyDown: handleTextKeyDown,
    className: s.hiddenInput,
    role: "img",
    "aria-required": ariaRequired,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedByIds
  }), externalImage);
};
//# sourceMappingURL=signature-drawer.js.map